import { addSeconds, isPast } from 'date-fns';
import { useEffect, useState, useMemo } from 'react';
import { selectSignedIn, SignInResponse } from 'reducers/auth';
import { useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import { DOMAIN } from 'endpoints.config';

export type TokenResource = {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpires: Date;
  tokenExpires: Date;
  scopes?: Scopes;
  profileInitials: string;
};

/**
 * Save token data in local storage.
 */
export function saveTokenResource(resource: TokenResource): void {
  if (window) {
    // AB#210768 - the access & refresh tokens now live in Secure HttpOnly cookies and are NOT
    // stored in localStorage (which is XSS-readable). We keep only non-sensitive markers here:
    // the expiry timestamps (for login-state), the user's initials and the navigation scopes.
    window.localStorage.setItem('ATE', resource.tokenExpires.toString());
    window.localStorage.setItem('RTE', resource.refreshTokenExpires.toString());
    // Only save real initials, as storing a missing value would show the text "null" in the header.
    if (resource.profileInitials) {
      window.localStorage.setItem('UI', resource.profileInitials);
    }
    if (resource.scopes) {
      window.localStorage.setItem('SCOPES', JSON.stringify(resource.scopes));
    }
  }
}

export type LoadedTokenResource = {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpires: Date;
  scopes?: string;
  profileInitials: string;
};

/**
 * Fetch token data from local storage.
 */
export function loadTokenResource(): null | LoadedTokenResource {

  try {
    // AB#210768 - login-state is derived from the (non-sensitive) expiry markers; the tokens
    // themselves are in HttpOnly cookies and are sent automatically with each request.
    const tokenExpiry = window.localStorage.getItem('ATE');
    const refreshExpiry = window.localStorage.getItem('RTE');
    const profileInitials = window.localStorage.getItem('UI');
    const scopes = window.localStorage.getItem('SCOPES');

    // If the login markers are missing, abort.
    if (tokenExpiry == null || refreshExpiry == null) {
      throw new Error();
    }

    const refreshExpiryDate = new Date(refreshExpiry);

    if (isPast(refreshExpiryDate)) {
      throw new Error()
    };

    return {
      accessToken: '',
      refreshToken: '',
      refreshTokenExpires: refreshExpiryDate,
      scopes: scopes ?? '[]',
      profileInitials: profileInitials ?? '',
    };
  } catch (_error) {
    localStorage.clear();
    return null;
  }
}

const cookies = new Cookies();

export const setAuthCookie = (data: any) => {
  const { expiresIn } = data.details;
  if (!expiresIn) return;
  const tokenExpires: Date = addSeconds(new Date(), parseInt(expiresIn, 10));
  cookies.set('GETSET_SchoolUser', 'sign-in', {
    expires: tokenExpires,
    domain: DOMAIN
  });
};

export const clearAuthCookie = () => {
  cookies.remove('GETSET_SchoolUser', { domain: DOMAIN });
};

export type AccessElement = {
  displayName: string;
  path: string;
  childElements?: Scopes;
};

export type Scopes = AccessElement[];

export function loadScopes(): string {
  try {
    const scopes = window.localStorage.getItem('SCOPES');
    if (scopes == null) throw new Error();

    return scopes;
  } catch (error) {
    return '[]';
  }
}

/**
 * Used to load navigation routes that are available to this user.
 * Effect runs on app load and sign-in.
 */
export function useLoadScopes(): Scopes {
  const [scopeString, setScopeString] = useState('[]');
  const isSignedIn = useSelector(selectSignedIn);

  useEffect(() => {
    if (isSignedIn) setScopeString(loadScopes());
  }, [isSignedIn]);

  const scopes: Scopes = useMemo(() => JSON.parse(scopeString), [scopeString]);

  return scopes;
}
